import HTMLTemplate from './template.html'
import './styles.scss'
import tippy, {followCursor} from 'tippy.js';
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-away.css'
import User from '../../modules/user'
import { ModalComponent } from '../Modal/Modal'
import { cloneDeep } from 'lodash'
import Routes from '../../modules/routes'
import { DetailsPanel } from '../DetailsPanel/DetailsPanel'
import MapBox from '../../modules/map'
import AeroSpace from '../../modules/aerospace'
import InternationalSOSAlerts from '../../modules/InternationalSOSAlerts'
import AvsecAlerts from '../../modules/avsecAlerts'
import Export from '../../modules/export'
import { getAirportDetailProperties } from '../../services/AirportService'
import ClientPortalAPIService from '../../services/api/ClientPortal/ApiService'
import { SearchBar } from '../SearchBar/SearchBar'
import Sentry from '../../modules/SantryIO'
import MixPanelService from '../../services/MixPanelService'

export const MyRoutes = (() => {
    const vueComponent = {
        data() {
            return {
                isLoading: true,
                routes: [],
                airports: [],
                routesSearchInput: '',
                covidRestrictions: {
                    countryRestrictions: [],
                    covidImpactDefinitions: {
                        travel: {},
                        domesticOperations: {}
                    }
                },
                userAirportCodePreference: 'ICAO'
            }
        },
        mounted() {
            this.subscribeToUserSettingsChanges()
        },
        methods: {
            initialize() {
                this.update()
            },
            update() {
                User.getLocalUserSettings().then(userSettings => {
                    let routes = userSettings.routes.map(routeJSONString => JSON.parse(routeJSONString))
                    const oldRoutes = routes.filter(route => route.decodeRouteResult[0].type === 'Origin Airport')
                    const oldRoutePromises = []
                    if (oldRoutes.length === 0) {
                        this.updateRoutes(routes)
                    } else {
                        // This console error can be used in the error reporting tool to see how many users are still using the old format of routes
                        // and once this drop down to 0 over some good amount of time, then this code can be removed
                        console.error('Old routes found, updating them to new format')

                        oldRoutes.forEach(route => {
                            oldRoutePromises.push(ClientPortalAPIService.decodeRoute(route.decodeRouteResult[0].name, route.routePlanString, route.decodeRouteResult[route.decodeRouteResult.length - 1].name))
                        })
                        
                        Promise.allSettled(oldRoutePromises).then(responses => {
                            const successfullyParsedRoutes = []
                            responses.forEach(response => {
                                if (response.status === 'fulfilled') {
                                    successfullyParsedRoutes.push(response.value)
                                } else {
                                    console.error('Error decoding old route', response)
                                }
                            })

                            routes = routes.map(route => {
                                const updatedRoute = successfullyParsedRoutes.find(parsedRoute => parsedRoute.data.routePlanString === route.routePlanString)
                                if (updatedRoute !== undefined) {
                                    return {
                                        decodeRouteResult: updatedRoute.data.decodeRouteResult,
                                        ...map
                                    }
                                } else {
                                    return route
                                }
                            })

                            this.updateRoutes(routes)

                            // This happens once all routes were decoded with the new format
                            // In this case we just override all old routes with the new system which should work as one time operation
                            User.getLocalUserSettings().then(userSettings => {
                                User.updateUserSettings({
                                    ...userSettings,
                                    routes: routes.map(route => JSON.stringify({
                                        decodeRouteResult: route.decodeRouteResult,
                                        routePlanString: route.routePlanString
                                    }))
                                })
                            })
                        })
                    }
                })
            },
            updateRoutes(routes) {
                // Helper function to validate airport data
                const hasValidAirports = (route) => {
                    return route.airportOfOrigin && (route.airportOfOrigin.icao || route.airportOfOrigin.iata) &&
                           route.airportOfDestination && (route.airportOfDestination.icao || route.airportOfDestination.iata)
                }

                // Create a Map of existing routes for O(1) lookups
                const existingRoutesMap = new Map()
                this.routes.forEach(route => {
                    if (hasValidAirports(route)) {
                        existingRoutesMap.set(route.routePlanString, route)
                    }
                })

                const routeInformationPromises = []
                const routeInformationPromisesRoutePlanStrings = []
                const processedRoutes = []

                // Process all routes in a single pass
                for (const route of routes) {
                    const airportOfOrigin = this.getAirportByICAOCode(route.decodeRouteResult[0].data.identifier)
                    const airportOfDestination = this.getAirportByICAOCode(route.decodeRouteResult[route.decodeRouteResult.length - 1].data.identifier)

                    route.airportOfOrigin = getAirportDetailProperties(airportOfOrigin)
                    route.airportOfDestination = getAirportDetailProperties(airportOfDestination)

                    // Only process routes with valid airports
                    if (!hasValidAirports(route)) {
                        continue
                    }

                    const existingRoute = existingRoutesMap.get(route.routePlanString)
                    
                    if (existingRoute === undefined) {
                        // New route - collect promise for route information
                        routeInformationPromises.push(Routes.getRouteInformation(route.decodeRouteResult))
                        routeInformationPromisesRoutePlanStrings.push(route.routePlanString)
                        processedRoutes.push(route)
                    } else {
                        // Existing route - preserve it (may have more complete data)
                        processedRoutes.push(existingRoute)
                    }
                }

                // Add any existing routes that weren't in the new input
                existingRoutesMap.forEach((existingRoute, routePlanString) => {
                    const isInProcessed = processedRoutes.some(r => r.routePlanString === routePlanString)
                    if (!isInProcessed) {
                        processedRoutes.push(existingRoute)
                    }
                })

                this.routes = processedRoutes

                // Create a Map for O(1) route lookup when assigning route information
                const routesMap = new Map()
                this.routes.forEach(route => {
                    routesMap.set(route.routePlanString, route)
                })

                Promise.all(routeInformationPromises).then(values => {
                    for (let index = 0; index < values.length; index++) {
                        const routePlanString = routeInformationPromisesRoutePlanStrings[index]
                        const route = routesMap.get(routePlanString)
                        if (route) {
                            route.routeInformation = values[index]
                        }
                    }
                }).finally(() => {
                    this.isLoading = false
                })
            },
            subscribeToUserSettingsChanges() {
                window.setInterval(() => this.userAirportCodePreference = User.userSettings.airportCode, 2500)
            },
            getAirportByICAOCode(ICAOCode) {
                return this.airports.find(airport => airport.ICAO === ICAOCode)
            },
            getAirportByIATACode(IATACode) {
                return this.airports.find(airport => airport.IATA === IATACode)
            },
            updateRouteSelections() {
                const selectedRoutes = this.routes.filter(route => !!route.selected)
                Routes.updateDisplay(cloneDeep(selectedRoutes))
            },
            removeRoute(routePlanString) {
                // TODO: For now let's use our routePlanString as ID of reference, this could be somehow improved, as it could be possible to have 2 exact same
                // route plans, but different origin and destination?
                User.removeRoute(routePlanString).then(() => {
                    // We removed it from user settings for simplicity remove it also from this module without fetching user settings again
                    const existingRouteIndex = this.routes.findIndex(route => route.routePlanString === routePlanString)
                    MixPanelService.track('Remove Route', { 
                        'Origin Airport': this.routes[existingRouteIndex].airportOfOrigin.icao,
                        'Destination Airport': this.routes[existingRouteIndex].airportOfDestination.icao
                    })
                    if (existingRouteIndex > -1) {
                        this.routes.splice(existingRouteIndex, 1)
                    }
                }).catch(error => Sentry.captureException(error))
            },
            exportRoute(route) {
                let originAirportData = { 
                    ...route.airportOfOrigin,
                    covidRating: {}
                }
                let destinationAirportData = { 
                    ...route.airportOfDestination,
                    covidRating: {}
                }

                const countryOriginCovidRestrictions = this.findCountryCovidRestrictions(originAirportData.countryCode3)
                if (countryOriginCovidRestrictions !== undefined) {
                    originAirportData.covidRating = countryOriginCovidRestrictions.impactRatings
                }

                const countryDestCovidRestrictions = this.findCountryCovidRestrictions(destinationAirportData.countryCode3)
                if (countryOriginCovidRestrictions !== undefined) {
                    destinationAirportData.covidRating = countryDestCovidRestrictions.impactRatings
                }

                MixPanelService.track('Download Route', { 
                    'Origin Airport': originAirportData.icao,
                    'Destination Airport': destinationAirportData.icao
                })

                Export.downloadReport('route', {
                    originAirport: originAirportData,
                    destinationAirport: destinationAirportData,
                    routeInformation: cloneDeep(route.routeInformation)
                })
            },
            findCountryCovidRestrictions(countryISO) {
                return this.covidRestrictions.countryRestrictions.find(country => {
                    return country.countryISO === countryISO
                })
            },
            showAddRouteModal() {
                ModalComponent.$data.showAddRouteModal = true
                setTimeout(() => ModalComponent.$refs.addRouteCode.focus(), 10)
            },
            selectAllRoutes() {
                this.routes.forEach(route => route.selected = true)
                this.updateRouteSelections()
            },
            deselectAllRoutes() {
                this.routes.forEach(route => route.selected = false)
                this.updateRouteSelections()
            },
            anySelectedRoutes() {
                return this.routes.some(route => !!route.selected)
            },
            openFIR(FIRdetails) {                
                AeroSpace.selectFIR(FIRdetails.icao, FIRdetails.ISO_3166_1_ALPHA_3, FIRdetails.name, FIRdetails.countries, null, null, null, FIRdetails.region, FIRdetails.name)

                SearchBar.showFIRLayers = true
            },
            openAirport(airportDetails) {
                MapBox.map.jumpTo({
                    center: [airportDetails.longitude, airportDetails.latitude],
                    zoom: 8
                })
                DetailsPanel.showDetailsPanel(airportDetails)
            },
            openAlert(alert, isAviationAlert) {
                if (!!isAviationAlert) {
                    AvsecAlerts.showCountryAlerts(alert.IsoCode)
                    console.log(alert)
                } else {
                    InternationalSOSAlerts.showAlert(alert.id)
                }
            }
        },
        template: HTMLTemplate
    }

    const DOMElement = document.getElementById('vue-my-routes')
    if (DOMElement === null) {
        return null
    } else {
        return Vue.createApp(vueComponent).use({
            install: (app) => {
                app.directive('tooltip', {
                    beforeMount (el, binding) {
                        tippy(el, {
                            content: binding.value,
                            placement: 'bottom-start',
                            animation: 'shift-away',
                            allowHTML: true,
                            trigger: 'mouseenter',
                            arrow: false,
                            followCursor: true,
                            plugins: [followCursor]
                        })
                    }
                })
            }
        }).mount(DOMElement)
    }
})()
